import * as React from 'react';
import * as BizCharts from 'bizcharts';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { DELIVERABLE_STATUSES } from '../../constants/DeliverableStatuses';

const { Chart, Axis, Geom, Tooltip, Legend } = BizCharts;

const ACTUAL_COLOR = '#73d13d';
const PROJECTED_COLOR = '#bfbfbf';

// "Budget vs Deliverables Completed": cumulative budget of completed deliverables
// (green) against a straight planned line to the full budget (grey dashed).
// Deliverables have no completedAt yet, so completion order falls back to id.
const BudgetVsDeliverablesChart = ({ deliverables = [], projectBudget, currency }: any) => {
	const fmt = (amount) =>
		currency ? currency.symbolFormat(amount, 0) : `$${(Number(amount) || 0).toLocaleString()}`;

	// Y-axis ticks only. A full currency string ($1,250,000) is wide enough to run
	// into the axis title, so ticks are abbreviated and the exact amounts stay in the
	// tooltip. The symbol comes from the same currency the tooltip formats with.
	const fmtTick = (amount) => {
		const value = Number(amount) || 0;
		// symbolFormat renders "<symbol><amount>", so formatting zero at zero digits
		// leaves the symbol once the "0" is dropped — no second source of truth.
		const symbol = fmt(0).replace('0', '');
		const abbreviate = (divisor, suffix) => {
			const scaled = value / divisor;
			// One decimal only while it says something: 1.2M, but 12M rather than 12.0M.
			return `${scaled < 10 ? scaled.toFixed(1).replace(/\.0$/, '') : Math.round(scaled)}${suffix}`;
		};
		if (Math.abs(value) >= 1000000) {
			return `${symbol}${abbreviate(1000000, 'M')}`;
		}
		if (Math.abs(value) >= 1000) {
			return `${symbol}${abbreviate(1000, 'k')}`;
		}
		return `${symbol}${Math.round(value)}`;
	};

	const completed = (deliverables || [])
		.filter((entry) => nullSafeGet('status', entry) === DELIVERABLE_STATUSES.completed)
		.sort((a, b) => nullSafeGetOrElse('id', a, 0) - nullSafeGetOrElse('id', b, 0));

	const totalDeliverables = (deliverables || []).length;
	const deliverablesBudget = (deliverables || []).reduce(
		(sum, entry) => sum + Number(nullSafeGetOrElse('budget', entry, 0)),
		0
	);
	const totalBudget = Number(projectBudget) || deliverablesBudget || 0;

	// Total-budget projects don't set per-deliverable budgets, so the cumulative
	// budget would sit flat at $0 for every completed deliverable. Fall back to a
	// proportional burn-up of the project budget by completion count.
	const useProportional = deliverablesBudget === 0 && totalBudget > 0;

	let cumulativeBudget = 0;
	let cumulativeUsed = 0;
	const actual = [{ x: 0, val: 0, series: 'Actual', name: '', used: 0, budget: 0 }];
	completed.forEach((entry, index) => {
		if (useProportional) {
			cumulativeBudget =
				totalDeliverables > 0 ? Math.round((totalBudget * (index + 1)) / totalDeliverables) : 0;
			cumulativeUsed = cumulativeBudget;
		} else {
			cumulativeBudget += Number(nullSafeGetOrElse('budget', entry, 0));
			cumulativeUsed += Number(nullSafeGetOrElse('budgetUsed', entry, 0));
		}
		actual.push({
			x: index + 1,
			val: cumulativeBudget,
			series: 'Actual',
			name: nullSafeGetOrElse('name', entry, `Deliverable ${index + 1}`),
			used: cumulativeUsed,
			budget: cumulativeBudget,
		});
	});

	const span = Math.max(totalDeliverables, completed.length, 1);
	const projected = [
		{ x: 0, val: 0, series: 'Projected' },
		{ x: span, val: totalBudget, series: 'Projected' },
	];

	const data = [...actual, ...projected];

	return (
		<Chart
			height={340}
			autoFit
			data={data}
			// A fixed gutter, NOT padding="auto": G2 accepts 'auto', but combined with
			// autoFit inside the overview's flex row it sized the canvas to the whole row
			// and the plot bled across the meta sidebar. 84px on the left clears the
			// abbreviated tick labels (~40px) plus the axis title at offset 60.
			padding={[24, 32, 64, 84]}
			scale={{
				x: { alias: 'Completed Deliverables', min: 0, nice: true, tickInterval: 1 },
				val: { alias: 'Budget (in USD)', min: 0, nice: true },
			}}
		>
			<Legend visible={false} />
			<Axis name="x" title={{ offset: 40 }} />
			<Axis name="val" title={{ offset: 60 }} label={{ formatter: (value) => fmtTick(value) }} />
			<Tooltip
				showCrosshairs
				shared
				customContent={(title, items) => {
					const point = (items || []).find((item) => nullSafeGet('data.series', item) === 'Actual');
					const record = point ? point.data : null;
					if (!record || !record.x) {
						return '';
					}
					return `<div style="background:#fff;border:1px solid #f0f0f0;border-radius:2px;padding:12px;min-width:220px;">
						<div style="font-weight:500;color:#262626;margin-bottom:8px;">${record.name || 'Deliverable'}</div>
						<div style="display:flex;justify-content:space-between;color:#595959;">
							<span>Total Budget</span><span>${fmt(record.used)} / ${fmt(record.budget)}</span>
						</div>
					</div>`;
				}}
			/>
			<Geom
				type="line"
				position="x*val"
				color={['series', [ACTUAL_COLOR, PROJECTED_COLOR]]}
				size={2}
				style={['series', (series) => ({ lineDash: series === 'Projected' ? [4, 4] : null })]}
			/>
			<Geom
				type="point"
				position="x*val"
				color={['series', [ACTUAL_COLOR, PROJECTED_COLOR]]}
				shape="circle"
				size={['series', (series) => (series === 'Actual' ? 4 : 0)]}
			/>
		</Chart>
	);
};

export default BudgetVsDeliverablesChart;
